import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { PlusIcon } from '@heroicons/react/24/solid'
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useMaintenanceRequests } from '@/hooks/useMaintenance'
import { useDateFormatter } from '@/hooks/useDateFormatter'

const STATUS_CLASSES: Record<string, string> = {
  open: 'status-badge-warning',
  in_progress: 'status-badge-info',
  completed: 'status-badge-success',
}

export default function MaintenancePage() {
  const { t } = useTranslation('maintenance')
  const { t: tc } = useTranslation('common')
  const { formatDate } = useDateFormatter()
  const { data: requests, isLoading, error } = useMaintenanceRequests()

  return (
    <div className="p-4 space-y-3 pb-safe">
      {isLoading && <LoadingSpinner />}

      {error && (
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      )}

      {!isLoading && !error && requests?.length === 0 && (
        <div className="text-center py-12 text-base-content/50">
          <div className="flex justify-center mb-3">
            <div className="icon-container">
              <WrenchScrewdriverIcon className="w-5 h-5" />
            </div>
          </div>
          <p>{t('no_requests')}</p>
          <Link to="/maintenance/new" className="btn btn-primary mt-4">
            {t('new_request')}
          </Link>
        </div>
      )}

      {requests?.map((req) => (
        <div key={req.id} className="flat-card p-4 space-y-2">
          <div className="flex justify-between items-start gap-2">
            <h3 className="font-semibold text-sm flex-1">{req.title}</h3>
            <span className={`${STATUS_CLASSES[req.status] ?? 'status-badge-ghost'} text-xs shrink-0`}>
              {tc(`status.${req.status}`)}
            </span>
          </div>
          {req.category && (
            <span className="status-badge-ghost">{req.category}</span>
          )}
          <p className="text-xs text-base-content/60">{formatDate(req.created_at)}</p>
        </div>
      ))}

      {/* Floating Action Button */}
      <Link
        to="/maintenance/new"
        className="fixed bottom-24 right-4 w-14 h-14 bg-primary text-primary-content rounded-full flex items-center justify-center shadow-lg z-10"
        aria-label={t('new_request')}
      >
        <PlusIcon className="w-6 h-6" />
      </Link>
    </div>
  )
}
